import { Settings } from '@streamr/tv-engine';
import { Track } from './Track';
const AVATAR_COLORS = [
    '#3a3a3a',
    '#2e2e2e',
    '#333333',
    '#383838',
    '#2b2b2b',
    '#353535',
    '#303030',
    '#3d3d3d',
];
export function getColorForId(id) {
    let hash = 0;
    for (let i = 0; i < id.length; i++) {
        hash = (hash * 31 + id.charCodeAt(i)) | 0;
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}
const PLACEHOLDER_WIDTH = Settings.videoEncoderDefaultWidth;
const PLACEHOLDER_HEIGHT = Settings.videoEncoderDefaultHeight;
function renderPlaceholderBitmap(displayName, userId, width = PLACEHOLDER_WIDTH, height = PLACEHOLDER_HEIGHT) {
    const canvas = new OffscreenCanvas(width, height);
    const ctx = canvas.getContext('2d');
    if (!ctx) {
        throw new Error('Failed to get 2D context from OffscreenCanvas');
    }
    ctx.fillStyle = getColorForId(userId);
    ctx.fillRect(0, 0, width, height);
    ctx.fillStyle = '#FFFFFF';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    const maxFontSize = Math.floor(width / 8);
    let fontSize = maxFontSize;
    ctx.font = `600 ${fontSize}px sans-serif`;
    const measured = ctx.measureText(displayName);
    const maxTextWidth = width * 0.85;
    if (measured.width > maxTextWidth) {
        fontSize = Math.floor(fontSize * (maxTextWidth / measured.width));
        ctx.font = `600 ${fontSize}px sans-serif`;
    }
    ctx.fillText(displayName, width / 2, height / 2, maxTextWidth);
    return canvas.transferToImageBitmap();
}
/**
 * A re-usable track that renders display-name placeholders as ImageBitmap frames.
 * Implements the ReadonlyTrack interface. Can be added to and removed from the
 * video engine multiple times during a user's online session.
 *
 * Lifecycle:
 * - Created when a user comes online (MediaManager.createPlaceholderTrack)
 * - Added to videoEngine when user has no real video (showPlaceholderTrack)
 * - Removed from videoEngine when real video arrives (hidePlaceholderTrack → hide())
 * - Re-added when real video stops (showPlaceholderTrack again)
 * - Destroyed when user goes offline (destroyPlaceholderTrack → destroy())
 */
export class PlaceholderTrack {
    trackId;
    userId;
    _isLocal;
    displayName;
    track;
    showing = false;
    destroyed = false;
    pendingHide = false;
    constructor(trackId, displayName, userId, isLocal = false) {
        this.trackId = trackId;
        this.displayName = displayName;
        this.userId = userId;
        this._isLocal = isLocal;
        this.track = new Track(trackId, false);
    }
    getTrackId() {
        return Promise.resolve(this.trackId);
    }
    isLocal() {
        return Promise.resolve(this._isLocal);
    }
    setListener(listener) {
        if (this.destroyed || this.pendingHide) {
            this.pendingHide = false;
            void listener(undefined, true);
            return;
        }
        this.track = new Track(this.trackId, false);
        this.track.setListener(listener);
        this.showing = true;
        this.emitPlaceholderFrame();
    }
    unsetListener() {
        this.track.unsetListener();
    }
    hasListener() {
        return this.track.hasListener();
    }
    getBufferLength() {
        return this.track.getBufferLength();
    }
    updateDisplayName(displayName) {
        this.displayName = displayName;
        if (this.showing) {
            this.emitPlaceholderFrame();
        }
    }
    hide() {
        this.pendingHide = true;
        if (this.showing) {
            this.track.emit(undefined, true);
            this.showing = false;
        }
    }
    clearPendingHide() {
        this.pendingHide = false;
    }
    destroy() {
        this.destroyed = true;
        this.hide();
    }
    emitPlaceholderFrame() {
        const bitmap = renderPlaceholderBitmap(this.displayName, this.userId);
        const frame = {
            frameNumber: 0n,
            timestamp: 0n,
            duration: 0n,
            trackId: this.trackId,
            done: false,
            isKeyFrame: false,
            codedWidth: PLACEHOLDER_WIDTH,
            codedHeight: PLACEHOLDER_HEIGHT,
            isScreenSharing: false,
            rotation: 0,
            videoFrame: bitmap,
        };
        this.track.emit(frame, false, [bitmap]);
    }
}
